import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import {
  Box,
  Flex,
  Heading,
  Button,
  ButtonGroup,
  Text,
  EmptyState,
  Spinner,
  Callout,
  showSuccessToast,
  showErrorToast
} from 'core/components';
import { Alert, Intent } from '@blueprintjs/core';
import styled, { useTheme } from 'styled-components';
import { useHistory, useRouteMatch } from 'react-router-dom';
import moment from 'moment';
import { PiMarkdownLogo } from 'react-icons/pi';
import Markdown from 'app/components/markdown/Markdown';
import SettingsPanelHeading from 'app/views/settings/ai/SettingsPanelHeading';
import MarkdownCodeViewer, { buildUnifiedDiff } from './MarkdownCodeViewer';
import { DEFAULT_USER_NAME, PLACEHOLDER_INSTRUCTIONS } from './utils';
import { markdownCodeStyles } from './sharedStyles';

const HeaderContainer = styled(Box)`
  margin-top: 0px;
  margin-bottom: 25px;
  flex-shrink: 0;
`;

const ContentBox = styled(Box)`
  border: none;
  border-radius: 4px;
  padding: 24px;
  background-color: transparent;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  margin-bottom: 0px;
`;

const StyledAlert = styled(Alert)`
  .bp3-dialog-header,
  .bp4-dialog-header,
  .bp5-dialog-header {
    background: transparent !important;
    border-bottom: none !important;
    box-shadow: none !important;
    padding-bottom: 0 !important;
  }
`;

const MarkdownContainer = styled(Box)`
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.body};

  ${markdownCodeStyles}
`;

const VersionHistoryButton = styled(Button)`
  background-color: ${({ theme }) =>
    theme.name === 'light' ? theme.colors.lightGray4 : 'rgba(255,255,255,0.1)'} !important;
  background-image: none !important;
  box-shadow: none !important;
  border: 1px solid ${({ theme }) => theme.colors.gray3 || 'rgba(16, 22, 26, 0.2)'} !important;
`;

const ViewContext = ({ versionId, collection, isSidebarOpen, onToggleSidebar }) => {
  const history = useHistory();
  const match = useRouteMatch();
  const theme = useTheme();
  const [viewMode, setViewMode] = useState('formatted');
  const [diffError, setDiffError] = useState(false);

  const [isRevertAlertOpen, setIsRevertAlertOpen] = useState(false);
  const [isReverting, setIsReverting] = useState(false);

  const versions = collection.models;

  const currentModel = collection.getVersionModel(versionId);

  const currentIndex = currentModel ? versions.indexOf(currentModel) : -1;
  const hasPriorVersion = currentIndex >= 0 && currentIndex < versions.length - 1;
  const isLatestVersion = versionId === 'latest' || currentIndex === 0;

  const prevModel = currentModel ? collection.getPreviousVersion(currentModel) : null;

  useEffect(() => {
    if (!currentModel || !currentModel.needsFetch) {
      return;
    }
    currentModel.fetchContent().catch(() => undefined);
  }, [currentModel]);

  useEffect(() => {
    setViewMode('formatted');
    setDiffError(false);
  }, [currentModel]);

  const fetchError = currentModel && currentModel.error;
  const isLoading = collection.isLoadingVersion(currentModel);
  const isMissingVersion = !isLoading && versionId !== 'latest' && !currentModel;

  const handleRevert = useCallback(() => {
    if (!currentModel) {
      return;
    }

    setIsReverting(true);

    collection
      .saveContext({
        context: currentModel.get('context'),
        comment: 'Reverted to previous revision',
        showToast: false
      })
      .then(() => {
        showSuccessToast('Successfully reverted Custom Network Context to previous revision.');
        setIsRevertAlertOpen(false);
        history.push(`${match.url}/latest`);
      })
      .catch((err) => {
        console.error('Failed to revert CNC:', err);
        showErrorToast(`Failed to revert Custom Network Context: ${err.message || 'Unknown error'}`);
      })
      .finally(() => {
        setIsReverting(false);
      });
  }, [collection, currentModel, history, match.url]);

  if (isLoading) {
    return (
      <Flex height="100%" alignItems="center" justifyContent="center">
        <Spinner size={30} />
      </Flex>
    );
  }

  if (fetchError) {
    return (
      <Box p={4}>
        <Callout intent="danger" icon="error" title="Error Loading Context">
          Failed to load Custom Network Context. Please try refreshing the page.
        </Callout>
      </Box>
    );
  }

  if (isMissingVersion) {
    return (
      <Box p={4}>
        <Callout intent="warning" icon="warning-sign" title="Version Not Found">
          The requested Custom Network Context version could not be found.
        </Callout>
      </Box>
    );
  }

  const modelData = currentModel ? currentModel.get() : {};
  const { context: currentContext = '', cdate, user } = modelData;

  const formattedDate =
    cdate && moment(cdate).isValid() ? moment(cdate).format('MMM D, YYYY - h:mm A') : 'Legacy Version';

  const userName = (user && user.user_full_name) || DEFAULT_USER_NAME;

  const hasNoData = !currentContext || currentContext.trim() === '';
  const hasEmptyRevision = currentModel && currentModel.hasContent && hasNoData;
  const showEmptyState = hasEmptyRevision || (!currentModel && hasNoData);

  const displayPrompt = currentContext;

  const renderDiffContent = () => {
    const prevIsFetching = prevModel && prevModel.isRequestActive('fetching');

    if (prevIsFetching) {
      return (
        <Flex flex={1} alignItems="center" justifyContent="center">
          <Spinner size={20} />
        </Flex>
      );
    }

    if (!prevModel || (!prevModel.hasContent && !diffError)) {
      return (
        <Flex flex={1} alignItems="center" justifyContent="center">
          <Spinner size={20} />
        </Flex>
      );
    }

    if (diffError || !prevModel.hasContent) {
      return (
        <Box flex={1} minHeight={0} display="flex" flexDirection="column">
          <Callout intent="warning" icon="warning-sign" title="Unable to Load Previous Version">
            The previous version could not be fetched. Try switching away and back to Diff.
          </Callout>
        </Box>
      );
    }

    const prevContext = prevModel.get('context');

    if (prevContext === currentContext) {
      return (
        <Box flex={1} mt={4}>
          <Callout intent="primary" icon="info-sign" title="No Changes Detected">
            The content of this version is completely identical to the previous version in the history.
          </Callout>
        </Box>
      );
    }

    return (
      <MarkdownCodeViewer
        value={buildUnifiedDiff(prevContext, currentContext)}
        mode={{ name: 'markdown', highlightFormatting: true }}
        isDiff
      />
    );
  };

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <StyledAlert
        className={theme.name === 'dark' ? 'bp3-dark bp4-dark bp5-dark' : ''}
        cancelButtonText="Cancel"
        confirmButtonText={isReverting ? 'Reverting...' : 'Confirm Rollback'}
        intent={Intent.PRIMARY}
        isOpen={isRevertAlertOpen}
        loading={isReverting}
        onCancel={() => setIsRevertAlertOpen(false)}
        onConfirm={handleRevert}
        title="Confirm CNC Rollback"
        style={{ width: '500px', maxWidth: '90vw' }}
      >
        <div style={{ lineHeight: '1.5', fontSize: '14px', padding: '4px 12px' }}>
          <p style={{ margin: '0 0 12px 0' }}>Are you sure you want to revert the Custom Network Context?</p>
          <p style={{ margin: 0 }}>
            This action will create a <strong>new revision</strong> with content identical to this selected revision.
            The current history will not be deleted.
          </p>
        </div>
      </StyledAlert>

      <HeaderContainer mt={0}>
        <Flex justifyContent="space-between" alignItems="start" my={3}>
          <SettingsPanelHeading level={2} m={0}>
            Custom Network Context
          </SettingsPanelHeading>

          {/* stays on the right when the heading is hidden */}
          <Flex alignItems="start" ml="auto">
            {!hasNoData && (
              <Box mr={3} textAlign="right">
                <Text as="div" fontSize="13px" color="muted">
                  Last Modified: <strong>{formattedDate}</strong>
                </Text>
                <Text as="div" fontSize="13px" color="muted">
                  by <strong>{userName}</strong>
                </Text>
              </Box>
            )}

            {isLatestVersion && currentModel && (
              <Button
                intent="primary"
                onClick={() => history.push(`${match.url}/edit`)}
                minWidth="100px"
                height="36px"
                fontSize="14px"
                fontWeight="bold"
              >
                <Text textTransform="uppercase">Edit</Text>
              </Button>
            )}

            {!isLatestVersion && currentModel && (
              <Button
                intent="primary"
                icon="history"
                onClick={() => setIsRevertAlertOpen(true)}
                minWidth="100px"
                height="36px"
                fontSize="14px"
                fontWeight="bold"
              >
                Revert to this version
              </Button>
            )}
          </Flex>
        </Flex>

        <Flex justifyContent="space-between" alignItems="center" width="100%">
          {!hasNoData && (
            <ButtonGroup>
              {['markdown', 'formatted', 'diff'].map((mode) => (
                <Button
                  key={mode}
                  active={viewMode === mode}
                  intent={viewMode === mode ? 'primary' : 'none'}
                  disabled={mode === 'diff' && !hasPriorVersion}
                  onClick={() => {
                    if (mode === 'diff' && prevModel) {
                      setDiffError(false);

                      if (prevModel.needsFetch) {
                        prevModel.fetchContent().catch(() => {
                          setDiffError(true);
                        });
                      }
                    }
                    setViewMode(mode);
                  }}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </Button>
              ))}
            </ButtonGroup>
          )}

          {!isSidebarOpen && versions.length > 0 && (
            <VersionHistoryButton icon="history" text="Version History" onClick={onToggleSidebar} />
          )}
        </Flex>
      </HeaderContainer>

      {showEmptyState && (
        <Flex alignItems="center" flexDirection="column">
          <EmptyState icon={<PiMarkdownLogo size={80} />} title="No Custom Network Context" />
          <Box maxWidth={800} width="100%" textAlign="left">
            <Heading level={6} textAlign="center" mb={2}>
              What is Custom Network Context?
            </Heading>
            <MarkdownContainer className="markdown-body">
              <Markdown>{PLACEHOLDER_INSTRUCTIONS}</Markdown>
            </MarkdownContainer>
            {!currentModel && (
              <Flex justifyContent="center">
                <Button icon="plus" intent="primary" onClick={() => history.push(`${match.url}/edit`)}>
                  Add
                </Button>
              </Flex>
            )}
          </Box>
        </Flex>
      )}

      {!hasNoData && viewMode === 'formatted' && (
        <ContentBox>
          <MarkdownContainer className="markdown-body">
            <Markdown hideCsvDownloadButton>{displayPrompt}</Markdown>
          </MarkdownContainer>
        </ContentBox>
      )}

      {!hasNoData && viewMode === 'markdown' && (
        <MarkdownCodeViewer
          value={displayPrompt}
          mode={{ name: 'markdown', highlightFormatting: true }}
          isDiff={false}
        />
      )}

      {!hasNoData && viewMode === 'diff' && renderDiffContent()}
    </Box>
  );
};

ViewContext.propTypes = {
  versionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  collection: PropTypes.object.isRequired,
  isSidebarOpen: PropTypes.bool,
  onToggleSidebar: PropTypes.func
};

ViewContext.defaultProps = {
  versionId: 'latest',
  isSidebarOpen: false,
  onToggleSidebar: () => {}
};

export default observer(ViewContext);
